import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { tokenService } from '../storage/tokenService';
import { API_BASE_URL, API_TIMEOUT, API_ENDPOINTS, HTTP_STATUS, ERROR_MESSAGES } from '../../utils/constants';
import type { AuthTokens } from '../../types/auth';

/**
 * Flag to prevent multiple simultaneous token refresh requests
 */
let isRefreshing = false;

/**
 * Queue of failed requests waiting for token refresh
 */
let failedRequestsQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

/**
 * Process queued requests after token refresh
 * @param error - Error if token refresh failed
 * @param token - New access token if refresh succeeded
 */
const processQueue = (error: any = null, token: string | null = null): void => {
  failedRequestsQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });

  failedRequestsQueue = [];
};

/**
 * Refresh the access token using the refresh token
 * @returns Promise with new tokens
 */
const refreshAccessToken = async (): Promise<AuthTokens> => {
  const refreshToken = tokenService.getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    // SimpleJWT with ROTATE_REFRESH_TOKENS=True returns a new refresh token
    // alongside the access. We MUST persist it because the old refresh is
    // blacklisted server-side after rotation; reusing it would 401 on the
    // next refresh and log the user out.
    const response = await axios.post<{ access: string; refresh?: string }>(
      `${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
      { refresh: refreshToken }
    );

    const newTokens: AuthTokens = {
      access: response.data.access,
      refresh: response.data.refresh ?? refreshToken,
    };

    tokenService.setTokens(newTokens);
    return newTokens;
  } catch (error) {
    tokenService.clearTokens();
    throw error;
  }
};

/**
 * Create and configure axios instance
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  /**
   * Request Interceptor
   * Adds Authorization header with access token to all requests
   */
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const accessToken = tokenService.getAccessToken();

      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      // FormData uploads: axios detecta y arma multipart correctamente
      // sólo si el Content-Type NO está pre-fijado. La instancia se
      // crea con `Content-Type: application/json` (default para el
      // 99% del tráfico), así que hay que retirarlo aquí para que la
      // subida de fichero llegue como multipart con boundary. Sin
      // esto, el backend recibe el file como string y DRF devuelve
      // «La información enviada no era un archivo».
      if (
        typeof FormData !== "undefined" &&
        config.data instanceof FormData &&
        config.headers
      ) {
        // AxiosHeaders.delete devuelve boolean; funciona igual en el
        // fallback objeto plano (cuando el interceptor cae en el
        // camino sin AxiosHeaders).
        (config.headers as any).delete?.("Content-Type");
        delete (config.headers as any)["Content-Type"];
      }

      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  /**
   * Response Interceptor
   * Handles 401 errors and automatic token refresh
   */
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Handle network errors
      if (!error.response) {
        return Promise.reject({
          message: ERROR_MESSAGES.NETWORK_ERROR,
          originalError: error,
        });
      }

      // Handle 401 Unauthorized - Token expired or invalid
      if (error.response.status === HTTP_STATUS.UNAUTHORIZED && originalRequest && !originalRequest._retry) {
        // Check if this is a refresh token request that failed
        if (originalRequest.url?.includes(API_ENDPOINTS.AUTH.REFRESH)) {
          tokenService.clearTokens();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // El login que falla con 401 significa "credenciales
        // inválidas" — no hay que intentar refrescar un token que
        // aún no existe. Sin esta rama, el interceptor caía en
        // `refreshAccessToken()`, tokenService devolvía null y
        // acabábamos propagando "No refresh token available" — un
        // mensaje que oculta el motivo real (usuario/pass mal).
        if (originalRequest.url?.includes(API_ENDPOINTS.AUTH.LOGIN)) {
          return Promise.reject({
            message: ERROR_MESSAGES.INVALID_CREDENTIALS,
            status: 401,
            originalError: error,
          });
        }

        // Mark request as retried to prevent infinite loops
        originalRequest._retry = true;

        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({
              resolve: (token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(client(originalRequest));
              },
              reject: (err: any) => {
                reject(err);
              },
            });
          });
        }

        isRefreshing = true;

        try {
          // Attempt to refresh the token
          const newTokens = await refreshAccessToken();
          
          // Update the authorization header with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
          }

          // Process queued requests with new token
          processQueue(null, newTokens.access);

          // Retry the original request
          return client(originalRequest);
        } catch (refreshError) {
          // Token refresh failed, clear tokens and redirect to login
          processQueue(refreshError, null);
          tokenService.clearTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Handle 403 Forbidden
      if (error.response.status === HTTP_STATUS.FORBIDDEN) {
        return Promise.reject({
          message: ERROR_MESSAGES.UNAUTHORIZED,
          originalError: error,
        });
      }

      // Handle 500+ Server Errors
      if (error.response.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
        return Promise.reject({
          message: ERROR_MESSAGES.SERVER_ERROR,
          originalError: error,
        });
      }

      // Handle other errors
      const errorMessage = error.response.data 
        ? (error.response.data as any).detail || (error.response.data as any).message || error.message
        : error.message;

      return Promise.reject({
        message: errorMessage,
        status: error.response.status,
        originalError: error,
      });
    }
  );

  return client;
};

/**
 * Configured axios instance for API requests
 * Includes automatic token management and error handling
 */
export const apiClient = createApiClient();

export default apiClient;
